import { createListenerMiddleware, TypedStartListening, UnknownAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "src/redux/store";
import { logDev } from "src/utils";
import { refreshContainerEntryDrawerState } from "./controlled_substance_logging/containerEntryDrawerSlice";
import { refreshContainerLogDrawerState } from "./controlled_substance_logging/containerLogDrawerSlice";
import {
    fetchContainerDetails,
    fetchLotDetails,
    refreshContainerLogsTable,
    refreshLotContainersTable,
} from "./controlled_substance_logging/lotAndContainerDetailsSlice";
import { refreshReconciliationState } from "./controlled_substance_logging/reconciliationDrawerSlice";
import { clearAllDrawers, closeAllDrawers, removeFromDrawerStack } from "./drawerStackSlice";
import {
    DEBUG_STACKABLE_DRAWER_MIDDLEWARE,
    isDrawerHideAction,
    isDrawerShowAction,
    isDrawerShowActionOrPending,
} from "./stackableDrawerConstant";

// ! README: documentation/development/stackable_drawer_system.md

interface DrawerAction {
    type: string;
    payload: { id: string; [key: string]: any };
}

export const getDrawerTypeFromAction = (action: UnknownAction): keyof RootState => {
    return action.type.split("/")[0] as keyof RootState;
};

export const drawerMiddleware = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;

export const startAppListening = drawerMiddleware.startListening as AppStartListening;

const rehydrateDrawerState = (api: any, drawerType: string, instanceData: any) => {
    api.dispatch({ type: `${drawerType}/rehydrateState`, payload: instanceData });
};

const drawerRefreshActions = {
    containerLogDrawer: (state, api) => {
        if (state.containerLogDrawer.show) {
            api.dispatch(refreshContainerLogDrawerState());
        }
    },
    lotAndContainerDetails: (state, api) => {
        const { lotId, containerId, show } = state.lotAndContainerDetails;
        if (show) {
            if (containerId) {
                api.dispatch(fetchContainerDetails(containerId));
                api.dispatch(refreshContainerLogsTable());
            } else if (lotId) {
                api.dispatch(fetchLotDetails(lotId));
                api.dispatch(refreshLotContainersTable());
            }
        }
    },
    reconciliationDrawer: (state, api) => {
        if (state.reconciliationDrawer.show) {
            api.dispatch(refreshReconciliationState());
        }
    },
    containerEntryDrawer: (state, api) => {
        if (state.containerEntryDrawer.show) {
            api.dispatch(refreshContainerEntryDrawerState());
        }
    },
};

// In the show action middleware:
startAppListening({
    predicate: isDrawerShowAction,
    effect: async (action, api) => {
        const drawerType = getDrawerTypeFromAction(action);
        const currentState = api.getState();
        const drawerStack = currentState.drawerStack.activeDrawers;
        const currentDrawer = currentState[drawerType];
        if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
            logDev("NNN Show Drawer Effect:", {
                action,
                drawerType,
                currentStack: drawerStack,
                currentDrawer,
            });
        }
        // Add new drawer to stack
        if (
            !drawerStack.find(
                (d) => d.drawerHistoryInstanceId === currentDrawer.drawerHistoryInstanceId,
            ) &&
            currentDrawer.drawerHistoryInstanceId
        ) {
            api.dispatch({
                type: "drawerStack/addToDrawerStack",
                payload: {
                    drawerHistoryInstanceId: currentDrawer.drawerHistoryInstanceId,
                    type: drawerType,
                    state: currentDrawer,
                    parentId: (action.payload as any).parentId,
                },
            });
        } else if (
            drawerStack.find(
                (d) => d.drawerHistoryInstanceId === currentDrawer.drawerHistoryInstanceId,
            )
        ) {
            api.dispatch({
                type: "drawerStack/bringToFront",
                payload: currentDrawer.drawerHistoryInstanceId,
            });
        }
    },
});

// In the hide action middleware:
startAppListening({
    predicate: isDrawerHideAction,
    effect: async (action, api) => {
        const state = api.getState();
        const drawerType = getDrawerTypeFromAction(action);
        const currentDrawer = state[drawerType];

        if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
            logDev("NNN Hide Drawer Effect - Initial:", {
                action,
                drawerType,
                currentDrawer,
                currentStack: state.drawerStack.activeDrawers,
            });
        }

        if (currentDrawer && "drawerHistoryInstanceId" in currentDrawer) {
            // Capture instance ID before reset
            const closingInstanceId = currentDrawer.drawerHistoryInstanceId;

            // Reset the current drawer state
            api.dispatch({ type: `${drawerType}/resetState` });

            // Remove the current drawer from the stack
            api.dispatch(removeFromDrawerStack(closingInstanceId));

            // Get updated state after removal
            const updatedState = api.getState();
            const activeDrawers = updatedState.drawerStack.activeDrawers;

            if (activeDrawers.length > 0) {
                const previousDrawer = activeDrawers[activeDrawers.length - 1];

                // Only rehydrate if it's a different instance
                if (previousDrawer.drawerHistoryInstanceId !== closingInstanceId) {
                    if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
                        logDev("NNN Hide Drawer Effect - Rehydrating:", {
                            previousDrawer,
                            drawerState: previousDrawer.state,
                        });
                    }
                    // First reset drawer state by dispatching its hide action
                    api.dispatch({ type: `${previousDrawer.type}/resetState` });
                    // Then Rehydrate using the stored state without resetting
                    api.dispatch({
                        type: `${previousDrawer.type}/rehydrateState`,
                        payload: {
                            ...previousDrawer.state,
                            drawerHistoryInstanceId: previousDrawer.drawerHistoryInstanceId,
                            show: true,
                        },
                    });

                    // Use previousDrawer directly rather than trying to find it by action.payload
                    const refreshAction =
                        drawerRefreshActions[
                            previousDrawer.type as keyof typeof drawerRefreshActions
                        ];
                    if (refreshAction) {
                        // Use the updated state to ensure the correct data is used to determine what should be refreshed
                        refreshAction(api.getState(), api);
                    }
                }
            }
        }
    },
});

// Handle bring to front actions
startAppListening({
    predicate: (action: any) => action.type === "drawerStack/bringToFront",
    effect: async (action, api) => {
        const state = api.getState();
        const drawerStack = state.drawerStack.activeDrawers;
        const drawer = drawerStack.find((d) => d.drawerHistoryInstanceId === action.payload);

        if (drawer) {
            if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
                logDev("NNN Bring to Front Effect:", {
                    action,
                    drawer,
                    drawerStack,
                });
            }
            rehydrateDrawerState(api, drawer.type, drawer.state.instanceData);
        }
    },
});

startAppListening({
    predicate: (action) => action.type === closeAllDrawers.type,
    effect: async (action, api) => {
        const state = api.getState();
        const drawerStack = state.drawerStack.activeDrawers;
        if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
            logDev("NNN Close All Drawers Effect:", { drawerStack });
        }
        // Get unique drawer types from the active drawers
        const activeDrawerTypes = [...new Set(drawerStack.map((drawer) => drawer.type))];
        // Reset each active drawer type
        activeDrawerTypes.forEach((drawerType) => {
            if (drawerType in state) {
                api.dispatch({ type: `${drawerType}/resetState` });
            }
        });
        // Clear all drawers from the stack
        api.dispatch(clearAllDrawers());
    },
});

/*
 * Middleware to save the state of the drawer before a SHOW action is dispatched
 * it is critical to run before any new show action is dispatched because other the drawer slice state will be updated for the new drawer instance prior to a snapshot being taked
 * the snapshot is used to update the drawer history stack with the current state of the drawer before the new drawer is shown thus allowing the drawer to be restored to its previous state when it is closed
 * E.G. not losing any progress when filling out a container log form
 */
export const drawerSnapshotMiddleware = (store) => (next) => (action) => {
    const isPendingShowActionOrRegularShow = isDrawerShowActionOrPending(action);
    if (!isPendingShowActionOrRegularShow) {
        return next(action);
    }
    const currentState = store.getState();
    const drawerStack = currentState.drawerStack.activeDrawers;
    if (drawerStack.length === 0) {
        return next(action);
    }
    const lastDrawer = drawerStack[drawerStack.length - 1];
    const fullCurrentState = JSON.parse(JSON.stringify(currentState[lastDrawer.type]));
    if (DEBUG_STACKABLE_DRAWER_MIDDLEWARE) {
        logDev("NNN [Snapshot Middleware] Saving drawer state before SHOW action:", {
            actionType: action.type,
            lastDrawer,
            savedState: fullCurrentState,
        });
    }
    // Update the drawer stack with the current state
    store.dispatch({
        type: "drawerStack/updateDrawerState",
        payload: {
            drawerHistoryInstanceId: lastDrawer.drawerHistoryInstanceId,
            state: fullCurrentState,
        },
    });
    return next(action);
};
